import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { CustomTable } from "@components/custom-table/table";
import { resetDelete } from "@helpers/custom-delete/controller/delete-slice";
import { url } from "@constants/url-constants";
import { resetError, resetState, resetSuccess } from "../controller/post-slice";
import { getPostList } from "../services/post-services";
import DeleteModal from "@components/custom-table/DeleteModal";
import PublishModel from "@components/custom-table/PublishModel";
import { resetPublish } from "../../publish/controller/publish.slice";
import { S3_BASE_URL } from "@constants/constants";
import { getPostType } from "@helpers/helpers";
import { POST_CREATE_ID, POST_DELETE_ID, POST_PUBLISH_ID, POST_UNPUBLISH_ID, POST_UPDATE_ID } from "../../../helpers/permission/constant-permissions";
import { STORAGE_KEY } from "../../../constants/constants";

export const Posts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const posts = useSelector((state) => state.post.posts);
  const pagination = useSelector((state) => state.post.pagination);
  const isLoading = useSelector((state) => state.post.loading);
  const responseMessage = useSelector((state) => state.post.responseMessage);
  const publishMessage = useSelector((state) => state.publish.responseMessage);
  const successMessage = useSelector((state) => state.post.successMessage);
  const deleteMessage = useSelector((state) => state.delete.successMessage);
  const currentPage = pagination?.current_page;
  const deleteUrl = url.posts;
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteData, setDeleteData] = useState({});
  const [publishModel, setPublishModel] = useState(false);
  const [publishData, setPublishData] = useState({});
  const userData = JSON.parse(localStorage.getItem(STORAGE_KEY.USER));

  useEffect(() => {
    if (responseMessage !== null) {
      dispatch(resetState());
    }
  }, [dispatch, responseMessage]);

  useEffect(() => {
    if (publishMessage !== null) {
      toast.success(publishMessage);
      dispatch(resetPublish());
    }
  }, [dispatch, publishMessage]);

  useEffect(() => {
    if (deleteMessage !== null) {
      toast.success(deleteMessage);
      dispatch(resetDelete());
    }
  }, [dispatch, deleteMessage]);

  useEffect(() => {
    if (successMessage !== null) {
      toast.success(successMessage);
      dispatch(resetSuccess());
    }
  }, [dispatch, successMessage]);

  const handleDeletePost = ({ id, url, deleted_at }) => {
    setDeleteModel(true);
    setDeleteData({ id, url, deleted_at });
  };

  const handlePublish = ({ id, type, published_at }) => {
    setPublishModel(true);
    setPublishData({ id, type, published_at });
  };

  const handleCreatePost = () => {
    navigate(`/posts/add`);
    dispatch(resetError());
  };

  const handleEditPost = ({ id, page }) => {
    navigate(`/posts/edit/${id}?current_page=${page}`);
    dispatch(resetError());
  };

  const columns = useMemo(
    () => {
      const cols = [
        {
          header: "Sorting ",
          accessorKey: "sorting",
          enableColumnFilter: false,
          enableSorting: true,
        },
        {
          header: "Posts",
          accessorKey: "title",
          enableColumnFilter: false,
          enableSorting: true,
        },
        {
          header: "Type",
          accessorKey: "type",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return getPostType(cellProps.row.original.type);
          },
        },
        {
          header: "Image",
          accessorKey: "thumbnail",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return cellProps.row.original.thumbnail.image_url ? (
              <img
                src={S3_BASE_URL + cellProps.row.original.thumbnail.image_url}
                alt=""
                className="avatar-sm rounded-circle img-thumbnail"
              />
            ) : null;
          },
        },



      ]
      if (userData?.permission_ids?.includes(POST_PUBLISH_ID) || userData?.permission_ids?.includes(POST_UNPUBLISH_ID)) {
        cols.push({
          header: "Publish/Unpublish",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return (
              <ul className="list-unstyled hstack gap-2  d-flex justify-content-center mb-0">
                <li
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="publish"
                >
                  <button
                    className={
                      !cellProps.row.original.published_at
                        ? "btn btn-sm btn-success"
                        : "btn btn-sm btn-secondary"
                    }
                    disabled={(!userData?.permission_ids?.includes(POST_PUBLISH_ID) && !cellProps.row.original.published_at) || (!userData?.permission_ids?.includes(POST_UNPUBLISH_ID) && cellProps.row.original.published_at)}
                    onClick={() => {
                      if (cellProps.row.original.deleted_at === null) {
                        handlePublish({
                          id: cellProps.row.original._id,
                          page: currentPage,
                          published_at: cellProps.row.original.published_at,
                          type: "post",
                        });
                      } else {
                        toast.error("Record Not Found");
                      }
                    }}
                  >
                    {!cellProps.row.original.published_at
                      ? " Publish "
                      : "Unpublish"}
                  </button>
                </li>
              </ul>
            );
          },
        },)
      }

      if (userData?.permission_ids?.includes(POST_DELETE_ID) || userData?.permission_ids?.includes(POST_UPDATE_ID)) {
        cols.push({
          header: "Action",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return (
              <ul className="list-unstyled hstack gap-2  d-flex justify-content-center mb-0">
                {userData?.permission_ids?.includes(POST_UPDATE_ID) && (< li data-bs-toggle="tooltip" data-bs-placement="top" title="Edit">
                  <button
                    className="btn btn-sm btn-soft-info"
                    onClick={() => {
                      if (cellProps.row.original.deleted_at === null) {
                        handleEditPost({
                          id: cellProps.row.original._id,
                          page: currentPage,
                        });
                      } else {
                        toast.error("Record Not Found");
                      }
                    }}
                    id={`edit-tooltip-${cellProps.row.original.id}`}
                  >
                    <i className="mdi mdi-pencil" />
                  </button>
                </li>)}

                {userData?.permission_ids?.includes(POST_DELETE_ID) && (<li
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={
                    cellProps.row.original.deleted_at === null
                      ? "Delete"
                      : "Restore"
                  }
                >
                  <button
                    className={`btn btn-sm ${cellProps.row.original.deleted_at == null
                      ? "btn btn-sm btn-soft-danger"
                      : "btn-soft-success"
                      }`}
                    // onClick={() => dispatch(deleteCountry(cellProps.row.original._id))}
                    onClick={() =>
                      handleDeletePost({
                        id: cellProps.row.original._id,
                        url: deleteUrl,
                        deleted_at: cellProps.row.original.deleted_at,
                      })
                    }
                    id={`delete-tooltip-${cellProps.row.original.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </button>
                </li>)}
              </ul>
            );
          },
        },)
      }
      return cols
    },
    [currentPage, deleteUrl]
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModel}
        onDeleteClick={handleDeletePost}
        onCloseClick={() => setDeleteModel(false)}
        data={deleteData}
      />
      <PublishModel
        show={publishModel}
        field="post"
        onPublishClick={handlePublish}
        onCloseClick={() => setPublishModel(false)}
        data={publishData}
      />
      <div className="page-content">
        <ToastContainer autoClose={2000} />
        <div className="container-fluid">
          {
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="">
                    <div className="d-flex align-items-center text-start">
                      <h5 className="mb-0 card-title flex-grow-1">
                        Posts List
                      </h5>
                    </div>
                  </CardBody>
                  <CardBody>
                    <CustomTable
                      loading={isLoading}
                      columns={columns}
                      data={posts || []}
                      isCustomPageSize={true}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      isAddButton={true}
                      isAddPermission={userData?.permission_ids?.includes(POST_CREATE_ID)}
                      page={currentPage}
                      isEpisode={false}
                      paginationCount={pagination?.total_count}
                      handleUserClick={handleCreatePost}
                      handleList={getPostList}
                      paginationLimit={pagination}
                      buttonClass="btn btn-success"
                      buttonName="Add"
                      theadClass="thead-light"
                      SearchPlaceholder="Search for ..."
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          }
        </div>
      </div>
    </React.Fragment>
  );
};
